import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { COLORS } from '../lib/styleGuide';

const Landing = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  // Function to handle navigation - guests can now browse without login
  const handleNavigation = (path) => {
    navigate(path);
  };

  return (
    <div className="min-h-screen">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/70 backdrop-blur-md border-b border-gray-200 flex items-center justify-between px-5 py-3">
        <div className="flex items-center space-x-3">
          <img src="/Vesta_Logo.svg" alt="Vesta Logo" className="w-12 h-12" />
          <h1 className="font-sora text-2xl font-semibold text-[#1b1b1b] leading-none mt-1">Vesta</h1>
        </div>
        <nav className="ml-auto">
          {currentUser ? (
            <button
              onClick={() => navigate('/fooddiscovery')}
              className="text-[#1b1b1b] ml-6 no-underline font-medium bg-transparent border-none cursor-pointer hover:opacity-80 transition-opacity"
            >
              Continue to App
            </button>
          ) : (
            <button
              onClick={() => handleNavigation('/signin')}
              className="text-[#1b1b1b] ml-6 no-underline font-medium bg-transparent border-none cursor-pointer hover:opacity-80 transition-opacity"
            >
              Sign In
            </button>
          )}
        </nav>
      </header>

      {/* Hero Section */}
      <section className="relative z-10 flex flex-col items-center justify-center text-center px-5 py-24 h-screen overflow-hidden">
        <img 
          src="/Landing_Hero.png" 
          alt="Vesta marketplace hero showing local food creators and community meals" 
          className="absolute inset-0 w-full h-full object-cover z-[-2]"
        />
        <div className="absolute inset-0 bg-black/60 z-[-1]"></div>
        
        <h2 className="font-sora text-5xl max-w-[900px] mb-5 text-white">
          Discover Food That Fits Your Life
        </h2>
        <p className="text-xl max-w-[650px] mb-10 text-white">
          No last-minute grocery runs. No impossible meal kits. Just real, local meals made by people who care.
        </p>
        <div className="flex flex-col sm:flex-row gap-4">
          <button
            onClick={() => handleNavigation('/fooddiscovery')}
            style={{ 
              background: 'linear-gradient(135deg, #f4a261 0%, #e76f51 50%, #f4a261 100%)',
              backgroundSize: '200% 200%',
              animation: 'gradientShift 3s ease infinite'
            }}
            className="text-white px-8 py-4 rounded-lg font-semibold text-lg cursor-pointer border-none no-underline hover:opacity-90 transition-opacity"
          >
            Start Exploring
          </button>
          <button
            onClick={() => handleNavigation('/signup')}
            className="text-white px-8 py-4 rounded-lg font-semibold text-lg cursor-pointer border-2 border-white bg-transparent hover:bg-white hover:text-gray-900 transition-all duration-300"
          >
            Become a Creator
          </button>
        </div>
      </section>

      {/* Scene 1: The Working Parent */}
      <section className="relative py-20 px-5 bg-white">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
          <div className="order-2 lg:order-1">
            <h3 className="font-sora text-4xl mb-6 text-gray-900">Family Dinner, Without the Stress</h3>
            <p className="text-lg text-gray-700 leading-relaxed mb-8">
              It's 6:00 p.m. on a Wednesday. You're racing home, kids are hungry, and the last thing you want is frozen pizza. Instead, you swing by your neighbor, Nonna Rosa's house — a sweet Italian grandma who cooks like family — and pick up a bubbling lasagna. Your kids eat better, and you breathe easier.
            </p>
            <div className="inline-block bg-orange-100 text-orange-800 px-4 py-2 rounded-full text-sm font-semibold">
              "Skip frozen. Serve real."
            </div>
          </div>
          <div className="order-1 lg:order-2">
            <img 
              src="/family-dinner-exchange.png" 
              alt="Warm doorstep exchange with Nonna Rosa handing lasagna to a smiling parent" 
              className="w-full h-96 object-cover rounded-2xl shadow-lg"
            />
          </div>
        </div>
      </section>

      {/* Scene 2: The Gym Buff */}
      <section className="relative py-20 px-5 bg-gray-50">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <img 
              src="/Athlete-Meal-Prep.png" 
              alt="Athlete meal prep with portioned, balanced meals" 
              className="w-full h-96 object-cover rounded-2xl shadow-lg"
            />
          </div>
          <div>
            <h3 className="font-sora text-4xl mb-6 text-gray-900">Fuel Your Goals, Without the Prep</h3>
            <p className="text-lg text-gray-700 leading-relaxed mb-8">
              You're disciplined. You track your macros, but meal prep eats into your weekends. With Vesta, a licensed dietician comes to your home and designs your weekly menu — portioned, balanced, and exactly on target. You get your time back without breaking your routine.
            </p>
            <div className="inline-block bg-green-100 text-green-800 px-4 py-2 rounded-full text-sm font-semibold">
              "Stay on track. Gain back time."
            </div>
          </div>
        </div>
      </section>

      {/* Scene 3: The Celebration */}
      <section className="relative py-20 px-5 bg-white">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
          <div className="order-2 lg:order-1">
            <h3 className="font-sora text-4xl mb-6 text-gray-900">From Meals to Memories</h3>
            <p className="text-lg text-gray-700 leading-relaxed mb-8">
              It's your best friend's bachelorette. You're not booking a stuffy restaurant. Instead, a local chef arrives at your home — three courses, professional wine pairing, laughter all night. Unforgettable, intimate, uniquely yours.
            </p>
            <div className="inline-block bg-purple-100 text-purple-800 px-4 py-2 rounded-full text-sm font-semibold">
              "Your event, elevated."
            </div>
          </div>
          <div className="order-1 lg:order-2">
            <img 
              src="/airbnb-bachelorette-party.png" 
              alt="Group of friends laughing, glasses raised around a cozy table" 
              className="w-full h-96 object-cover rounded-2xl shadow-lg"
            />
          </div>
        </div>
      </section>

      {/* Scene 4: The 9–5 Professional */}
      <section className="relative py-20 px-5 bg-gray-50">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <img 
              src="/meal-prep-lunch.png" 
              alt="Professional opening a fresh meal bowl at their desk" 
              className="w-full h-96 object-cover rounded-2xl shadow-lg"
            />
          </div>
          <div>
            <h3 className="font-sora text-4xl mb-6 text-gray-900">No More Sad Desk Lunches</h3>
            <p className="text-lg text-gray-700 leading-relaxed mb-8">
              You're tired of sad desk lunches. A local food prepper in your neighborhood already makes healthy bowls for themselves — now, they make one for you too. Fresh, ready, no decisions required.
            </p>
            <div className="inline-block bg-blue-100 text-blue-800 px-4 py-2 rounded-full text-sm font-semibold">
              "Work smarter. Eat better."
            </div>
          </div>
        </div>
      </section>

      {/* Pivot Section */}
      <section className="relative py-20 px-5 bg-gradient-to-br from-orange-50 to-red-50">
        <div className="max-w-4xl mx-auto text-center">
          <h3 className="font-sora text-4xl mb-6 text-gray-900">Imagine If It Was Your Food</h3>
            <p className="text-lg text-gray-700 leading-relaxed mb-8 max-w-3xl mx-auto">
              Love what you're finding? Imagine sharing what you make. If your bread, your bowls, your sauces weren't just for your family — but for neighbors who crave what you make. With Vesta, you can share your passion with your community.
            </p>
          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-white rounded-2xl p-6 shadow-lg">
              <img 
                src="/couple-sharing-dinner.png" 
                alt="Foodie enjoying a meal" 
                className="w-full h-64 object-cover rounded-xl"
              />
            </div>
            <div className="bg-white rounded-2xl p-6 shadow-lg">
              <img 
                src="/At-home-chef.png" 
                alt="Creator plating food" 
                className="w-full h-64 object-cover rounded-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Scene 5: The Food Creator's Life */}
      <section className="relative py-20 px-5 bg-white">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
          <div className="order-2 lg:order-1">
            <h3 className="font-sora text-4xl mb-6 text-gray-900">From "You Should Sell This" to Sold Out</h3>
            <p className="text-lg text-gray-700 leading-relaxed mb-8">
              Maybe you're a weekend baker who sells out of sourdough by noon. Maybe you're a trained chef ready to host dinners in your city. Maybe you're just tired of hearing 'you should sell this' without a way to make it real. Vesta makes it simple — compliant, visible, and community-focused.
            </p>
            <div className="inline-block bg-orange-100 text-orange-800 px-4 py-2 rounded-full text-sm font-semibold">
              "Your food. Your rules. Your business."
            </div>
          </div>
          <div className="order-1 lg:order-2">
            <img 
              src="/Sourdough-baker.png" 
              alt="Creator plating a dish, smiling as they hand it to a customer" 
              className="w-full h-96 object-cover rounded-2xl shadow-lg"
            />
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="relative py-16 px-5 bg-gray-100">
        <div className="max-w-4xl mx-auto text-center">
          <h3 className="font-sora text-3xl mb-6 text-gray-900">About Vesta</h3>
          <p className="text-lg text-gray-700 leading-relaxed mb-8">
            Vesta is a legitimate marketplace connecting local food creators with their communities. 
            We're committed to food safety, local business support, and building genuine connections 
            between neighbors through shared meals.
          </p>
          <div className="grid md:grid-cols-3 gap-6 text-sm text-gray-600">
            <div>
              <h4 className="font-semibold mb-2">Food Safety First</h4>
              <p>All creators follow local health department guidelines and food safety standards.</p>
            </div>
            <div>
              <h4 className="font-semibold mb-2">Local Community</h4>
              <p>Supporting local businesses and building stronger neighborhood connections.</p>
            </div>
            <div>
              <h4 className="font-semibold mb-2">Transparent Platform</h4>
              <p>Clear policies, honest reviews, and transparent communication.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Closing CTA Band */}
      <section className="relative py-16 px-5 bg-gray-900 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h3 className="font-sora text-3xl mb-8">Ready for a Fresh Take on Meals?</h3>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8">
              <h4 className="font-sora text-xl mb-4">Ready to eat better?</h4>
              <p className="text-gray-300 mb-6">Discover meals that fit your life, made by people who care.</p>
              <button
                onClick={() => handleNavigation('/fooddiscovery')}
                style={{ 
                  background: 'linear-gradient(135deg, #f4a261 0%, #e76f51 50%, #f4a261 100%)',
                  backgroundSize: '200% 200%',
                  animation: 'gradientShift 3s ease infinite'
                }}
                className="w-full text-white px-6 py-3 rounded-lg font-semibold transition-opacity duration-300 hover:opacity-90"
              >
                Browse Local Meals
              </button>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8">
              <h4 className="font-sora text-xl mb-4">Ready to share yours?</h4>
              <p className="text-gray-300 mb-6">Share your passion with your community, on your terms.</p>
              <button
                onClick={() => handleNavigation('/signup')}
                style={{ 
                  background: 'linear-gradient(135deg, #f4a261 0%, #e76f51 50%, #f4a261 100%)',
                  backgroundSize: '200% 200%',
                  animation: 'gradientShift 3s ease infinite'
                }}
                className="w-full text-white px-6 py-3 rounded-lg font-semibold transition-opacity duration-300 hover:opacity-90"
              >
                Become a Creator
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Landing; 